'use client';
import { createContext, useReducer, ReactNode } from 'react';
import UserProvider, {
  UserContextValue
} from '@/components/context/UserContext/UserContext';
import ClosetProvider, {
  INITIAL_CLOSET_VALUE,
  ClosetContextValue
} from '@/components/context/ClosetContext/ClosetContext';
import WishlistProvider, {
  INITIAL_WISHLIST_VALUE,
  WishlistContextValue,
  WishlistItem,
  WishlistMap
} from '@/components/context/WishlistContext/WishlistContext';
import PaymentProvider, {
  INITIAL_PAYMENT_VALUE,
  PaymentContextValue
} from '@/components/context/PaymentContext/PaymentContext';
import CartProvider, {
  CartContextValue,
  INITIAL_CART_VALUE
} from '@/components/context/CartContext/CartContext';
import { applyCartWishlistFlags } from '@/util/cart_generic_client';
import SignupProvider from '@/components/context/SignupContext/SignupContext';
import { DodgeballProvider } from '@/components/context/DodgeballContext/DodgeballContext';
import { INITIAL_SIGNUP_VALUE, SignupContextValue } from '@/types/Signup';
import { User } from '@/types/User';
import SearchProvider from '../SearchContext/SearchContext';
import FlagsProvider, { FlagsContextValue } from '../FlagsContext/FlagsContext';
import { ProductCardData } from '@/types/Product';

export type ContextBrokerValue = {
  user: UserContextValue | undefined;
  wishlist: WishlistContextValue;
  closet: ClosetContextValue;
  payment: PaymentContextValue;
  cart: CartContextValue;
  signup: SignupContextValue;
  isIOSWebView: boolean;
  searchValue: null | string;
  flags: FlagsContextValue;
};

export const ContextBrokerCTX = createContext<ContextBrokerValue>({
  user: undefined,
  wishlist: INITIAL_WISHLIST_VALUE,
  closet: INITIAL_CLOSET_VALUE,
  payment: INITIAL_PAYMENT_VALUE,
  cart: INITIAL_CART_VALUE,
  signup: INITIAL_SIGNUP_VALUE,
  isIOSWebView: false,
  searchValue: null,
  flags: {}
});

type ContextBrokerProps = {
  children: ReactNode;
  hydratedState?: State;
};

type State = ContextBrokerValue;

export type BrokerAction =
  | { type: 'USER_LOGIN'; payload: User }
  | { type: 'USER_LOGOUT' }
  | { type: 'USER_UPDATE'; userData: User }
  | { type: 'USER_REFRESH'; newContextValue: UserContextValue }
  | { type: 'WISHLIST_ADD_ITEM'; wishlistEntry: WishlistItem }
  | { type: 'WISHLIST_REMOVE_ITEM'; wishlistItemId: string }
  | { type: 'WISHLIST_REFRESH'; newContextValue: WishlistContextValue }
  | { type: 'WISHLIST_CLEAR' }
  | { type: 'WISHLIST_SET_TEMP'; tempWishlist: WishlistItem[] }
  | { type: 'WISHLIST_ADD_ITEM_TEMP'; wishlistEntry: WishlistItem }
  | { type: 'WISHLIST_REMOVE_ITEM_TEMP'; productSku: string }
  | { type: 'WISHLIST_REFRESH_TEMP'; newContextValue: WishlistContextValue }
  | { type: 'WISHLIST_CLEAR_TEMP' }
  | { type: 'CLOSET_REFRESH'; newContextValue: ClosetContextValue }
  | { type: 'CLOSET_CLEAR' }
  | { type: 'CART_REFRESH'; newContextValue: CartContextValue }
  | { type: 'CART_CLEAR' }
  | { type: 'PAYMENT_REFRESH'; newContextValue: PaymentContextValue }
  | { type: 'PAYMENT_CLEAR' }
  | { type: 'SEARCH'; searchValue: null | string }
  | { type: 'SEARCH_CLEAR' }
  | { type: 'SIGNUP_PARTIAL_UPDATE'; payload: Partial<SignupContextValue> }
  | { type: 'SIGNUP_CLEAR' };

const reducer = (state: State, action: BrokerAction): State => {
  switch (action.type) {
    case 'USER_LOGIN': {
      return {
        ...state,
        user: action.payload
      };
    }

    case 'USER_LOGOUT': {
      return {
        ...state,
        user: undefined
      };
    }

    case 'USER_UPDATE': {
      return {
        ...state,
        user: {
          ...state.user,
          ...action.userData
        }
      };
    }

    case 'USER_REFRESH': {
      return {
        ...state,
        user: action.newContextValue
      };
    }

    case 'WISHLIST_ADD_ITEM': {
      const newWishlistArray = [
        ...state.wishlist?.wishlist,
        action.wishlistEntry
      ];
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          wishlist: newWishlistArray,
          wishlistAsMap: {
            ...state.wishlist?.wishlistAsMap,
            [action.wishlistEntry.sku]: true
          },
          itemCount: newWishlistArray.length
        }
      };
    }

    case 'WISHLIST_REMOVE_ITEM': {
      const itemToRemove = state.wishlist?.wishlist.find((item) => {
        return item.wishlistItemId === action.wishlistItemId;
      });

      if (!itemToRemove) {
        console.warn(
          `[WishlistContext - removeWishlistItem] - Tried to remove ${action.wishlistItemId} from wishlist, but this item does not exist on current user's wishlist`
        );
      }

      const newWishlistArray = state.wishlist?.wishlist.filter(
        (wishlistItem) => {
          return wishlistItem.wishlistItemId !== action.wishlistItemId;
        }
      );
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          wishlist: newWishlistArray,
          itemCount: newWishlistArray?.length,
          wishlistAsMap: itemToRemove
            ? {
                ...state.wishlist?.wishlistAsMap,
                [itemToRemove.sku]: false
              }
            : state.wishlist?.wishlistAsMap
        }
      };
    }

    case 'WISHLIST_REFRESH': {
      return {
        ...state,
        wishlist: action.newContextValue
      };
    }

    case 'WISHLIST_CLEAR': {
      return {
        ...state,
        wishlist: { ...INITIAL_WISHLIST_VALUE }
      };
    }

    case 'WISHLIST_SET_TEMP': {
      const newTempWishlistAsMap = action.tempWishlist.reduce(
        (map, item) => ({
          ...map,
          [item.sku]: true
        }),
        {}
      );
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          tempWishlist: action.tempWishlist,
          tempWishlistAsMap: newTempWishlistAsMap
        }
      };
    }

    case 'WISHLIST_ADD_ITEM_TEMP': {
      const newTempWishlist = [
        ...state.wishlist.tempWishlist,
        action.wishlistEntry
      ];
      const newTempWishlistAsMap = {
        ...state.wishlist.tempWishlistAsMap,
        [action.wishlistEntry.sku]: true
      };
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          tempWishlist: newTempWishlist,
          tempWishlistAsMap: newTempWishlistAsMap
        }
      };
    }

    case 'WISHLIST_REMOVE_ITEM_TEMP': {
      const newTempWishlist = state.wishlist.tempWishlist.filter(
        (item) => item.sku !== action.productSku
      );
      const newTempWishlistAsMap = {
        ...state.wishlist.tempWishlistAsMap,
        [action.productSku]: false
      };
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          tempWishlist: newTempWishlist,
          tempWishlistAsMap: newTempWishlistAsMap
        }
      };
    }

    case 'WISHLIST_CLEAR_TEMP': {
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          tempWishlist: [],
          tempWishlistAsMap: {}
        }
      };
    }

    case 'CLOSET_REFRESH': {
      return {
        ...state,
        closet: action.newContextValue
      };
    }

    case 'CLOSET_CLEAR': {
      return {
        ...state,
        closet: { ...INITIAL_CLOSET_VALUE }
      };
    }

    case 'CART_REFRESH': {
      return {
        ...state,
        cart: applyCartWishlistFlags(action.newContextValue)
      };
    }

    case 'CART_CLEAR': {
      return {
        ...state,
        cart: { ...INITIAL_CART_VALUE }
      };
    }

    case 'PAYMENT_REFRESH': {
      return {
        ...state,
        payment: action.newContextValue
      };
    }

    case 'PAYMENT_CLEAR': {
      return {
        ...state,
        payment: { ...INITIAL_PAYMENT_VALUE }
      };
    }

    case 'SIGNUP_PARTIAL_UPDATE': {
      return {
        ...state,
        signup: {
          ...state.signup,
          ...action.payload
        }
      };
    }

    case 'SIGNUP_CLEAR': {
      return {
        ...state,
        signup: { ...INITIAL_SIGNUP_VALUE }
      };
    }

    case 'SEARCH': {
      return {
        ...state,
        searchValue: action.searchValue
      };
    }

    case 'SEARCH_CLEAR': {
      return {
        ...state,
        searchValue: null
      };
    }

    default: {
      return state;
    }
  }
};

const initialState: State = {
  wishlist: {
    itemCount: 0,
    wishlist: [],
    wishlistAsMap: {},
    tempWishlist: [],
    tempWishlistAsMap: {}
  },
  closet: {
    closet: null
  },
  payment: {
    customer: null,
    method: null
  },
  cart: {
    ...INITIAL_CART_VALUE
  },
  signup: {
    ...INITIAL_SIGNUP_VALUE
  },
  isIOSWebView: false,
  user: undefined,
  searchValue: null,
  flags: {}
};

function ContextBroker(props: ContextBrokerProps) {
  const { children, hydratedState } = props;
  const [state, dispatch] = useReducer(reducer, hydratedState || initialState);

  return (
    <ContextBrokerCTX.Provider value={state}>
      <UserProvider dispatch={dispatch}>
        <PaymentProvider dispatch={dispatch}>
          <ClosetProvider dispatch={dispatch}>
            <WishlistProvider dispatch={dispatch}>
              <CartProvider dispatch={dispatch}>
                <SignupProvider dispatch={dispatch}>
                  <SearchProvider dispatch={dispatch}>
                    <FlagsProvider>
                      <DodgeballProvider>{children}</DodgeballProvider>
                    </FlagsProvider>
                  </SearchProvider>
                </SignupProvider>
              </CartProvider>
            </WishlistProvider>
          </ClosetProvider>
        </PaymentProvider>
      </UserProvider>
    </ContextBrokerCTX.Provider>
  );
}

export default ContextBroker;
